import { useCallback, useEffect, useState } from "react";
import { toast } from "react-hot-toast";
import {
  CheckCircle,
  Clock,
  Copy,
  Mail,
  XCircle,
  Ban,
  ShieldOff,
} from "lucide-react";
import { DataTable, DataTablePagination } from "@/components/ui/data-table";
import type { ColumnDef } from "@tanstack/react-table";
import {
  onboardingAdminService,
  OnboardingPaymentAdmin,
  OnboardingPaymentStatus,
} from "@/services/admin/onboarding.admin.service";

function formatMoney(cents: number, currency = "usd") {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

function formatDate(value: string | null) {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-US", {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function getStatusBadgeColor(status: OnboardingPaymentStatus) {
  switch (status) {
    case "PAID":
      return "bg-green-100 dark:bg-green-500/15 text-green-800 dark:text-green-300";
    case "PENDING":
    case "LINK_OPENED":
    case "PROCESSING":
      return "bg-yellow-100 dark:bg-yellow-500/15 text-yellow-800 dark:text-yellow-300";
    case "FAILED":
    case "CANCELLED":
      return "bg-red-100 dark:bg-red-500/15 text-red-800 dark:text-red-300";
    case "REFUNDED":
      return "bg-muted text-foreground";
    default:
      return "bg-muted text-foreground";
  }
}

function StatusBadge({ status }: { status: OnboardingPaymentStatus }) {
  return (
    <div
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(status)}`}
    >
      {status === "PAID" && <CheckCircle className="w-3 h-3 mr-1" />}
      {(status === "PENDING" ||
        status === "LINK_OPENED" ||
        status === "PROCESSING") && <Clock className="w-3 h-3 mr-1" />}
      {(status === "FAILED" || status === "CANCELLED") && (
        <XCircle className="w-3 h-3 mr-1" />
      )}
      {status}
    </div>
  );
}

export default function AdminOnboarding() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [formError, setFormError] = useState("");
  const [creating, setCreating] = useState(false);

  const [items, setItems] = useState<OnboardingPaymentAdmin[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState<
    OnboardingPaymentStatus | ""
  >("");
  const [search, setSearch] = useState("");
  const [searchInput, setSearchInput] = useState("");

  const [detail, setDetail] = useState<OnboardingPaymentAdmin | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [actionId, setActionId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await onboardingAdminService.list({
        page,
        limit,
        status: statusFilter,
        search,
      });
      setItems(res.items);
      setTotal(res.pagination.total);
      setTotalPages(res.pagination.totalPages);
    } catch {
      toast.error("Failed to load onboarding payments");
    } finally {
      setLoading(false);
    }
  }, [page, limit, statusFilter, search]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setFormError("");
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFormError("Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      setFormError("Enter a valid email address");
      return;
    }

    try {
      setCreating(true);
      const created = await onboardingAdminService.create({
        email: trimmedEmail,
        name: name.trim() || undefined,
      });
      setEmail("");
      setName("");
      try {
        await navigator.clipboard.writeText(created.url);
        toast.success("Payment link created and copied to clipboard");
      } catch {
        toast.success("Payment link created");
      }
      setPage(1);
      await load();
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string | string[] } } })
          ?.response?.data?.message;
      const text = Array.isArray(message)
        ? message.join(", ")
        : message || "Failed to create payment link";
      setFormError(text);
      toast.error(text);
    } finally {
      setCreating(false);
    }
  };

  const copyLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copied");
    } catch {
      toast.error("Could not copy link");
    }
  };

  const openDetail = async (id: string) => {
    try {
      setDetailLoading(true);
      const data = await onboardingAdminService.getOne(id);
      setDetail(data);
    } catch {
      toast.error("Failed to load payment details");
    } finally {
      setDetailLoading(false);
    }
  };

  const handleResend = async (id: string) => {
    try {
      setActionId(id);
      await onboardingAdminService.resend(id);
      toast.success("Payment link email resent");
      await load();
      if (detail?.id === id) {
        await openDetail(id);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to resend email";
      toast.error(message);
    } finally {
      setActionId(null);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm("Revoke this payment link? The recipient will no longer be able to pay.")) {
      return;
    }
    try {
      setActionId(id);
      await onboardingAdminService.revoke(id);
      toast.success("Payment link revoked");
      await load();
      if (detail?.id === id) {
        await openDetail(id);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to revoke link";
      toast.error(message);
    } finally {
      setActionId(null);
    }
  };

  const handleRevokeTrial = async (id: string) => {
    if (
      !confirm(
        "Revoke trial access? The activation code is deactivated and, if it was already redeemed, the brand's Shopify subscription is cancelled and they are sent back to pricing."
      )
    ) {
      return;
    }
    try {
      setActionId(id);
      await onboardingAdminService.revokeTrial(id);
      toast.success("Trial access revoked");
      await load();
      if (detail?.id === id) {
        await openDetail(id);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data
          ?.message || "Failed to revoke trial access";
      toast.error(message);
    } finally {
      setActionId(null);
    }
  };

  const canResend = (p: OnboardingPaymentAdmin) =>
    !["PAID", "CANCELLED", "REFUNDED"].includes(p.status) && !p.usedAt;

  const canRevoke = (p: OnboardingPaymentAdmin) =>
    !["PAID", "CANCELLED", "REFUNDED"].includes(p.status);

  const canRevokeTrial = (p: OnboardingPaymentAdmin) =>
    !!p.trialOtp && !p.trialRevokedAt;

  const columns: ColumnDef<OnboardingPaymentAdmin>[] = [
    {
      id: "email",
      header: "Email",
      // Sized to its longest value; the table scrolls rather than clipping.
      meta: { fit: true },
      cell: ({ row }) => (
        <div className="font-medium text-foreground">{row.original.email}</div>
      ),
    },
    {
      id: "name",
      header: "Name",
      meta: { width: "w-36" },
      cell: ({ row }) => (
        <div className="text-sm text-muted-foreground truncate">
          {row.original.name || "—"}
        </div>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      meta: { width: "w-28" },
      cell: ({ row: { original: p } }) => (
        <span className="text-foreground">
          {formatMoney(p.amountPaidCents ?? p.amountCents, p.currency)}
        </span>
      ),
    },
    {
      id: "status",
      header: "Status",
      meta: { width: "w-32" },
      cell: ({ row }) => <StatusBadge status={row.original.status} />,
    },
    {
      id: "createdAt",
      header: "Created",
      meta: { width: "w-40" },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(row.original.createdAt)}
        </span>
      ),
    },
    {
      id: "paidAt",
      header: "Paid at",
      meta: { width: "w-40" },
      cell: ({ row }) => (
        <span className="text-sm text-muted-foreground">
          {formatDate(row.original.paidAt)}
        </span>
      ),
    },
    {
      id: "actions",
      header: "Actions",
      cell: ({ row: { original: p } }) => (
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={() => copyLink(p.url)}
            className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:bg-accent text-muted-foreground"
            title="Copy link"
          >
            <Copy className="w-3 h-3" />
            Copy
          </button>
          {canResend(p) && (
            <button
              type="button"
              disabled={actionId === p.id}
              onClick={() => handleResend(p.id)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-border hover:bg-accent text-muted-foreground disabled:opacity-50"
              title="Resend email"
            >
              <Mail className="w-3 h-3" />
              Resend
            </button>
          )}
          {canRevoke(p) && (
            <button
              type="button"
              disabled={actionId === p.id}
              onClick={() => handleRevoke(p.id)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-red-200 dark:border-red-500/30 hover:bg-red-50 dark:hover:bg-red-500/10 text-red-700 dark:text-red-300 disabled:opacity-50"
              title="Revoke link"
            >
              <Ban className="w-3 h-3" />
              Revoke
            </button>
          )}
          {canRevokeTrial(p) && (
            <button
              type="button"
              disabled={actionId === p.id}
              onClick={() => handleRevokeTrial(p.id)}
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded border border-red-200 dark:border-red-500/30 hover:bg-red-50 dark:hover:bg-red-500/10 text-red-700 dark:text-red-300 disabled:opacity-50"
              title="Deactivate the trial code and cancel any subscription it granted"
            >
              <ShieldOff className="w-3 h-3" />
              Revoke trial
            </button>
          )}
          {p.trialRevokedAt && (
            <span
              className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-muted text-muted-foreground"
              title={`Trial revoked ${formatDate(p.trialRevokedAt)}`}
            >
              <ShieldOff className="w-3 h-3" />
              Trial revoked
            </span>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-foreground">Onboarding</h1>
        <p className="text-sm text-muted-foreground">
          Create one-time $1,000 Stripe checkout links and track payment status.
        </p>
      </div>

      <form
        onSubmit={handleCreate}
        className="bg-card border border-border rounded-xl p-6 space-y-4"
      >
        <h2 className="text-lg font-medium text-foreground">
          Create checkout link
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label
              htmlFor="onboarding-email"
              className="block text-sm font-medium text-muted-foreground mb-1"
            >
              Recipient email <span className="text-red-500">*</span>
            </label>
            <input
              id="onboarding-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="user@example.com"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/60"
            />
          </div>
          <div>
            <label
              htmlFor="onboarding-name"
              className="block text-sm font-medium text-muted-foreground mb-1"
            >
              Name <span className="text-muted-foreground">(optional)</span>
            </label>
            <input
              id="onboarding-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Jane Doe"
              className="w-full px-3 py-2 border border-border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#d7785b]/60"
            />
          </div>
        </div>
        {formError && (
          <p className="text-sm text-red-600 dark:text-red-400">{formError}</p>
        )}
        <div className="flex items-center gap-3">
          <button
            type="submit"
            disabled={creating}
            className="inline-flex items-center px-4 py-2 rounded-lg bg-[#d7785b] text-white hover:bg-[#c86c51] transition disabled:opacity-50"
          >
            {creating ? (
              <>
                <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Creating...
              </>
            ) : (
              "Create & email link"
            )}
          </button>
          <span className="text-xs text-muted-foreground">
            Fixed amount: $1,000.00 USD · single use
          </span>
        </div>
      </form>

      <div className="bg-card rounded-xl border border-border shadow-sm overflow-hidden">
        <div className="p-4 border-b border-border flex flex-wrap gap-3 items-center justify-between">
          <h2 className="text-lg font-medium text-foreground">Payments</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="search"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setPage(1);
                  setSearch(searchInput.trim());
                }
              }}
              placeholder="Search email or name"
              className="px-3 py-1.5 border border-border rounded-lg text-sm w-48"
            />
            <button
              type="button"
              onClick={() => {
                setPage(1);
                setSearch(searchInput.trim());
              }}
              className="px-3 py-1.5 text-sm rounded-lg border border-border hover:bg-accent"
            >
              Search
            </button>
            <select
              value={statusFilter}
              onChange={(e) => {
                setPage(1);
                setStatusFilter(
                  e.target.value as OnboardingPaymentStatus | ""
                );
              }}
              className="px-3 py-1.5 border border-border rounded-lg text-sm"
            >
              <option value="">All statuses</option>
              <option value="PENDING">PENDING</option>
              <option value="LINK_OPENED">LINK_OPENED</option>
              <option value="PROCESSING">PROCESSING</option>
              <option value="PAID">PAID</option>
              <option value="FAILED">FAILED</option>
              <option value="CANCELLED">CANCELLED</option>
              <option value="REFUNDED">REFUNDED</option>
            </select>
          </div>
        </div>

        <DataTable
          data={items}
          columns={columns}
          isLoading={loading}
          emptyText="No onboarding payments yet. Create a link above."
          onRowClick={(p) => openDetail(p.id)}
        />

        <DataTablePagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={limit}
          onPageChange={setPage}
          pageSizeOptions={[10, 20, 50]}
          onPageSizeChange={(size) => {
            setLimit(size);
            setPage(1);
          }}
        />
      </div>

      {(detail || detailLoading) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-card rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-4 border-b border-border">
              <h3 className="text-lg font-semibold text-foreground">
                Payment details
              </h3>
              <button
                type="button"
                onClick={() => setDetail(null)}
                className="text-muted-foreground hover:text-foreground text-sm"
              >
                Close
              </button>
            </div>
            {detailLoading && !detail ? (
              <div className="p-8 text-center text-muted-foreground">Loading...</div>
            ) : detail ? (
              <div className="p-4 space-y-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <div className="text-muted-foreground">Email</div>
                    <div className="font-medium">{detail.email}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Name</div>
                    <div className="font-medium">{detail.name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Status</div>
                    <StatusBadge status={detail.status} />
                  </div>
                  <div>
                    <div className="text-muted-foreground">Amount</div>
                    <div className="font-medium">
                      {formatMoney(
                        detail.amountPaidCents ?? detail.amountCents,
                        detail.currency
                      )}
                    </div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Created</div>
                    <div>{formatDate(detail.createdAt)}</div>
                  </div>
                  <div>
                    <div className="text-muted-foreground">Paid at</div>
                    <div>{formatDate(detail.paidAt)}</div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-muted-foreground">Checkout session</div>
                    <div className="font-mono text-xs break-all">
                      {detail.stripeCheckoutSessionId || "—"}
                    </div>
                  </div>
                  <div className="col-span-2">
                    <div className="text-muted-foreground">Payment intent</div>
                    <div className="font-mono text-xs break-all">
                      {detail.stripePaymentIntentId || "—"}
                    </div>
                  </div>
                  {detail.lastError && (
                    <div className="col-span-2">
                      <div className="text-muted-foreground">Last error</div>
                      <div className="text-red-600 dark:text-red-400">{detail.lastError}</div>
                    </div>
                  )}
                  {detail.trialOtp && (
                    <div className="col-span-2">
                      <div className="text-muted-foreground">Activation code</div>
                      <div className="flex items-center gap-2">
                        <code className="font-mono text-xs bg-muted border border-border rounded px-2 py-1">
                          {detail.trialOtp.code}
                        </code>
                        <span className="text-xs text-muted-foreground">
                          {detail.trialOtp.trialDays}-day trial
                        </span>
                        {detail.trialRevokedAt ? (
                          <span className="text-xs text-red-600 dark:text-red-400">
                            Revoked {formatDate(detail.trialRevokedAt)}
                          </span>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {detail.trialOtp.isActive ? "Active" : "Deactivated"}
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                  <div className="col-span-2">
                    <div className="text-muted-foreground mb-1">Payment link</div>
                    <div className="flex gap-2">
                      <code className="flex-1 text-xs bg-muted border border-border rounded px-2 py-1 break-all">
                        {detail.url}
                      </code>
                      <button
                        type="button"
                        onClick={() => copyLink(detail.url)}
                        className="px-2 py-1 text-xs border rounded hover:bg-accent"
                      >
                        Copy
                      </button>
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="text-sm font-semibold text-foreground mb-2">
                    Webhook events
                  </h4>
                  {!detail.events?.length ? (
                    <p className="text-sm text-muted-foreground">
                      No webhook events recorded yet.
                    </p>
                  ) : (
                    <ul className="space-y-2">
                      {detail.events.map((ev) => (
                        <li
                          key={ev.id}
                          className="border border-border rounded-lg p-3 text-xs"
                        >
                          <div className="flex flex-wrap justify-between gap-2 mb-1">
                            <span className="font-medium text-foreground">
                              {ev.type}
                            </span>
                            <span
                              className={
                                ev.status === "PROCESSED"
                                  ? "text-green-700 dark:text-green-300"
                                  : ev.status === "ERROR"
                                    ? "text-red-700 dark:text-red-300"
                                    : "text-muted-foreground"
                              }
                            >
                              {ev.status}
                              {ev.signatureVerified ? " · verified" : ""}
                            </span>
                          </div>
                          <div className="text-muted-foreground">
                            {formatDate(ev.receivedAt)} · {ev.stripeEventId}
                          </div>
                          {ev.error && (
                            <div className="text-red-600 dark:text-red-400 mt-1">{ev.error}</div>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      )}
    </div>
  );
}
